/**
 * BrokerIntelligenceHub — navy+gold rebuild (KOPS-465 / KOPS-511).
 *
 * Route: /broker-intelligence/* (mounted in Routes.tsx — swapped at end of migration).
 * Replaces: components/BrokerIntelligence/BrokerIntelligenceHub.tsx (old glassmorphism).
 *
 * Tab bar: 10 tabs (Dashboard, Agents, Transactions, Compliance, Documents,
 * AI Review, Billing, Commission, Import, Settings). Transactions + Compliance
 * are new first-class tabs that absorb the old components/broker-portal/ content.
 *
 * URL scheme: /broker-intelligence/{tabKey}?id={selectedId}
 * Tab component is responsible for honoring ?id=... for deep-linking into a
 * selected row on split-panel tabs.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  faChartLine,
  faUsers,
  faFileContract,
  faShieldAlt,
  faFolderOpen,
  faBolt,
  faStamp,
  faDollarSign,
  faHandHoldingDollar,
  faDownload,
  faCog,
  faBuilding,
  faComments,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../helpers/api';
import { hasModuleAccess } from '../../utils/permissions';
import HubShell, { HubMetric, HubTab, GoldButton, OutlineButton } from './shared/HubShell';
import { colors, fonts } from './shared/colors';

import DashboardTab from './tabs/DashboardTab';
import AgentsTab from './tabs/AgentsTab';
import TransactionsTab from './tabs/TransactionsTab';
import ComplianceTab from './tabs/ComplianceTab';
import DocumentsTab from './tabs/DocumentsTab';
import AIReviewTab from './tabs/AIReviewTab';
import DocumentReviewTab from '../broker-portal/tabs/DocumentReviewTab'; // KOPS-654: queue-based broker doc review, integrated into the hub
import BillingTab from './tabs/BillingTab';
import CommissionTab from './tabs/CommissionTab';
import ImportTab from './tabs/ImportTab';
import SettingsTab from './tabs/SettingsTab';
import CoachingTab from './tabs/CoachingTab';
import BulletinTab from './tabs/BulletinTab';

const TABS: HubTab[] = [
  { key: 'dashboard',    label: 'Dashboard',    icon: faChartLine },
  { key: 'agents',       label: 'Agents',       icon: faUsers },
  { key: 'coaching',     label: 'Coaching',     icon: faComments },
  { key: 'transactions', label: 'Transactions', icon: faFileContract },
  { key: 'compliance',   label: 'Compliance',   icon: faShieldAlt },
  { key: 'documents',    label: 'Documents',    icon: faFolderOpen },
  { key: 'ai-review',    label: 'AI Review',    icon: faBolt },
  { key: 'doc-review',   label: 'Doc Review',   icon: faStamp },
  { key: 'bulletin',    label: 'Bulletin',     icon: faComments },
  { key: 'billing',      label: 'Billing',      icon: faDollarSign },
  { key: 'commission',   label: 'Commission',   icon: faHandHoldingDollar },
  { key: 'import',       label: 'Import',       icon: faDownload },
  { key: 'settings',     label: 'Settings',     icon: faCog },
];

const TAB_LABELS: Record<string, string> = TABS.reduce(
  (acc, t) => ({ ...acc, [t.key]: t.label }),
  {} as Record<string, string>
);

interface BrokerageContext {
  brokerage_id: string;
  brokerage_name: string;
  plan_type?: string;
  agent_count?: number;
  monthly_revenue?: number;
}

const BrokerIntelligenceHub: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // --- Access gate ---------------------------------------------------------
  // Simplified plan/role check. Guards direct-URL access even if the user
  // skipped the filtered nav.
  //
  // Reads user from THREE sources because the codebase has two different
  // auth flows (CRM sign-in stores 'user', agent portal sign-in stores
  // 'user_info'), and internal-employee detection in AgentDashboardModern
  // decodes the JWT directly. We union all three for max compatibility.
  const currentUser = useMemo(() => {
    const merged: any = {};

    // Source 1: 'user' key (set by hooks/useAuth.ts after /auth/me)
    try {
      const stored = localStorage.getItem('user');
      if (stored) Object.assign(merged, JSON.parse(stored));
    } catch {
      /* ignore */
    }

    // Source 2: 'user_info' key (set by agent portal + CRM sign-in flows)
    try {
      const stored = localStorage.getItem('user_info');
      if (stored) Object.assign(merged, JSON.parse(stored));
    } catch {
      /* ignore */
    }

    // Source 3: JWT payload (matches AgentDashboardModern.tsx isInternalEmployee)
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Merge JWT claims — but don't overwrite fields set by Source 1/2 above
        if (payload.role && !merged.role) merged.role = payload.role;
        if (payload.user_role && !merged.user_role) merged.user_role = payload.user_role;
        if (payload.email && !merged.email) merged.email = payload.email;
        if (payload.id && !merged.id) merged.id = payload.id;
        if (payload.subscription && !merged.subscription) merged.subscription = payload.subscription;
      }
    } catch {
      /* ignore */
    }

    return Object.keys(merged).length > 0 ? merged : null;
  }, []);

  // Log once so we can verify field shape when debugging access issues
  useEffect(() => {
    if (currentUser) {
      console.log('🔐 [BrokerIntelligenceHub] current user:', {
        role: currentUser.role || currentUser.user_role,
        subscription: currentUser.subscription || currentUser.subscription_plan,
        paymentStatus: currentUser.paymentStatus || currentUser.payment_status,
        permissions: currentUser.permissions,
      });
    } else {
      console.warn('🔐 [BrokerIntelligenceHub] no user found in localStorage or JWT');
    }
  }, [currentUser]);

  const hasAccess = useMemo(
    () => hasModuleAccess('brokerIntelligenceV2', currentUser || undefined),
    [currentUser]
  );

  // --- Tab routing from URL path -------------------------------------------
  // Supports both /broker-intelligence/* (production) and /broker-intelligence-v2/*
  // (dev preview during migration). Base path is detected from current location.
  const getBasePath = (): string => {
    const parts = location.pathname.split('/').filter(Boolean);
    if (parts[0] === 'broker-intelligence-v2') return '/broker-intelligence-v2';
    return '/broker-intelligence';
  };

  const getTabFromPath = (): string => {
    const parts = location.pathname.split('/').filter(Boolean);
    const baseIdx = parts.findIndex(
      (p) => p === 'broker-intelligence' || p === 'broker-intelligence-v2'
    );
    const candidate = baseIdx >= 0 ? parts[baseIdx + 1] : undefined;
    return candidate && TABS.some((t) => t.key === candidate) ? candidate : 'dashboard';
  };

  const [activeTab, setActiveTab] = useState<string>(getTabFromPath());

  useEffect(() => {
    const newTab = getTabFromPath();
    if (newTab !== activeTab) setActiveTab(newTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const base = getBasePath();
    const target = key === 'dashboard' ? base : `${base}/${key}`;
    navigate(target);
  };

  // --- Brokerage context fetch ---------------------------------------------
  const [brokerage, setBrokerage] = useState<BrokerageContext | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrokerage = async () => {
      try {
        // Fast path: cached from previous session
        const cachedId = localStorage.getItem('brokerageId') || '';
        const cachedName = localStorage.getItem('brokerageName') || '';
        if (cachedId && cachedName) {
          setBrokerage({ brokerage_id: cachedId, brokerage_name: cachedName });
        }

        // Authoritative fetch
        const profileRes = await axiosInstance.get('/broker/company-profile');
        if (profileRes.data?.success && profileRes.data?.data) {
          const id = profileRes.data.data.brokerage_id as string;
          const name = profileRes.data.data.company_name as string;
          localStorage.setItem('brokerageId', id);
          localStorage.setItem('brokerageName', name);

          // Get aggregated stats for the strip
          let agentCount = 0;
          let monthlyRevenue = 0;
          let planType: string | undefined;
          try {
            const billingRes = await axiosInstance.get(`/broker/billing/${id}`);
            if (billingRes.data?.success) {
              agentCount = billingRes.data.billing?.current_month?.agent_counts?.total || 0;
              monthlyRevenue = billingRes.data.billing?.current_month?.total_amount || 0;
              planType = billingRes.data.billing?.plan_type;
            }
          } catch {
            /* billing is best-effort; strip still renders without it */
          }

          setBrokerage({
            brokerage_id: id,
            brokerage_name: name,
            plan_type: planType,
            agent_count: agentCount,
            monthly_revenue: monthlyRevenue,
          });
        }
      } catch (err) {
        // Hub still renders without brokerage context; tabs show their own errors
        console.warn('[BrokerIntelligenceHub] brokerage context fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBrokerage();
  }, []);

  // --- Stats strip metrics --------------------------------------------------
  const metrics: HubMetric[] = useMemo(() => {
    if (!brokerage) return [];
    const items: HubMetric[] = [
      {
        icon: faBuilding,
        value: brokerage.brokerage_name || '—',
        label: brokerage.plan_type ? brokerage.plan_type.toUpperCase() : 'BROKERAGE',
        color: colors.gold2,
      },
      {
        icon: faUsers,
        value: brokerage.agent_count ?? 0,
        label: 'Agents',
        color: colors.blue,
      },
      {
        icon: faDollarSign,
        value: `$${(brokerage.monthly_revenue ?? 0).toLocaleString()}`,
        label: 'MRR',
        color: colors.green,
      },
    ];
    return items;
  }, [brokerage]);

  const actions = (
    <>
      <GoldButton onClick={() => navigate('/broker-portal')}>Broker Portal</GoldButton>
      <OutlineButton onClick={() => (window.location.href = '/agent-portal/documents')}>
        📄 My Transactions
      </OutlineButton>
      <OutlineButton onClick={() => window.location.reload()}>Refresh</OutlineButton>
      <GoldButton onClick={() => navigate(`${getBasePath()}/agents`)}>+ Invite Agent</GoldButton>
    </>
  );

  // KOPS-654: the /sponsor/dashboard is, for a brokerage, the Coaching Console — where they manage
  // leads and agent invitations. Lives in the TOP NAV (next to Dashboard / Sign Out).
  const rightNavContent = (
    <GoldButton onClick={() => navigate('/sponsor/dashboard')}>🎯 Coaching Console</GoldButton>
  );

  // --- Render active tab body ----------------------------------------------
  const renderTab = () => {
    const brokerageId = brokerage?.brokerage_id;
    const brokerageName = brokerage?.brokerage_name;
    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab brokerageId={brokerageId} brokerageName={brokerageName} />;
      case 'agents':
        return <AgentsTab brokerageId={brokerageId} />;
      case 'coaching':
        return <CoachingTab brokerageId={brokerageId} />;
      case 'transactions':
        return <TransactionsTab brokerageId={brokerageId} />;
      case 'compliance':
        return <ComplianceTab brokerageId={brokerageId} />;
      case 'documents':
        return <DocumentsTab brokerageId={brokerageId} brokerageName={brokerageName} />;
      case 'ai-review':
        return <AIReviewTab brokerageId={brokerageId} brokerageName={brokerageName} />;
      case 'doc-review':
        return <DocumentReviewTab brokerageId={brokerageId} />;
      case 'bulletin':
        return <BulletinTab brokerageId={brokerageId} />;
      case 'billing':
        return <BillingTab brokerageId={brokerageId} data={brokerage} />;
      case 'commission':
        return <CommissionTab brokerageId={brokerageId} />;
      case 'import':
        return <ImportTab brokerageId={brokerageId} />;
      case 'settings':
        return <SettingsTab brokerageId={brokerageId} data={brokerage} />;
      default:
        return <DashboardTab brokerageId={brokerageId} brokerageName={brokerageName} />;
    }
  };

  // Access denied shell — same navy+gold chrome, centered denial message.
  if (!hasAccess) {
    return (
      <div
        style={{
          background: colors.navy,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: fonts.body,
        }}
      >
        <link
          href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap"
          rel="stylesheet"
        />
        <div
          style={{
            background: colors.navy2,
            border: `1px solid ${colors.navy3}`,
            borderLeft: `4px solid ${colors.gold}`,
            borderRadius: '12px',
            padding: '48px 56px',
            maxWidth: '480px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          <h1
            style={{
              fontFamily: fonts.display,
              fontSize: '36px',
              color: colors.white,
              margin: '0 0 12px 0',
              letterSpacing: '1px',
            }}
          >
            ACCESS RESTRICTED
          </h1>
          <p style={{ color: colors.muted, fontSize: '14px', margin: '0 0 24px 0', lineHeight: 1.6 }}>
            Broker Intelligence Hub is available to broker-plan subscribers and KairosIQ internal
            employees. Contact your brokerage admin or support to request access.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`,
              border: 'none',
              borderRadius: '6px',
              padding: '10px 24px',
              color: colors.navy,
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <HubShell
      breadcrumb={`Broker Intelligence / ${TAB_LABELS[activeTab] || 'Dashboard'}`}
      homeHref="/agent-portal/dashboard"
      metrics={metrics}
      actions={actions}
      rightNavContent={rightNavContent}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      {loading && !brokerage ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: colors.offBg,
            color: colors.muted,
            fontSize: '14px',
          }}
        >
          Loading brokerage...
        </div>
      ) : (
        renderTab()
      )}
    </HubShell>
  );
};

export default BrokerIntelligenceHub;
